import * as React from 'react';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';
import type {
    GetQuoteById_quote as QuoteType,
    GetInvoiceById_invoice as InvoiceType,
} from 'nutshell-graphql-types';

import {ActionMenu} from '../action-menu/action-menu';
import {MoreOptionsButton} from '../button/more-options-button/more-options-button';
import {Dialog} from '../dialog/dialog';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from '../icon/svg/email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from '../icon/svg/eye-icon';
import {FileTextIcon} from '../icon/svg/file-text-icon';
import {HtmlLinkIcon} from '../icon/svg/html-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {SendIcon} from '../icon/svg/send-icon';
import {SelectMenu} from '../select-menu/select-menu';
import {Body} from '../typography/body';

import {
    getIsPrimaryActionVisible,
    getQuoteActionMenuOptions,
    getInvoiceActionMenuOptions,
} from './helpers';
import {QuotePrimaryActionButton} from './quote-primary-action-button';

import styles from './quote.module.css';

const NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID = 'nutshell-quote-script-container';

type Props = {
    quote: QuoteType | InvoiceType;
    handleQuoteDownload: (slug: string, scriptRef: any, anchor: string) => void;
    handleUpdateStatus: (status: QuoteDisplayStatus) => void;
    handleDuplicateQuote: () => void;
    handleSendActionMenuClose: () => void;
    handleSendEmail: () => void;
    handleCreateInvoiceFromQuote?: () => void;
};

export function QuoteOverviewHeader(props: Props) {
    const {quote, handleSendActionMenuClose, handleQuoteDownload, handleUpdateStatus} = props;

    const [isArchivingPopoverOpen, setIsArchivingPopoverOpen] = React.useState(false);
    const [isViewingPopover, setIsViewingPopover] = React.useState(false);
    const viewMenuRef = React.useRef<HTMLElement | null | undefined>(null);
    const scriptRef = React.useRef<HTMLElement | null | undefined>(null);
    const actionMenuRef = React.useRef<HTMLElement | null | undefined>(null);

    const getCopyLinkOption = React.useCallback(
        (triggerOnboarding = false) => {
            return {
                icon: <HtmlLinkIcon size={14} />,
                label: `Copy link to ${quote.__typename.toLowerCase()}`,
                key: 'copy',
                handleSelect: () => {
                    navigator.clipboard.writeText(quote.shareableViewInBrowserLink);
                    if (triggerOnboarding) {
                        handleSendActionMenuClose();
                    }
                },
            };
        },
        [quote.shareableViewInBrowserLink, quote.__typename, handleSendActionMenuClose]
    );

    const getDownloadPdfOption = React.useCallback(
        (triggerOnboarding = false) => {
            return {
                icon: <FileTextIcon size={14} />,
                label: 'Download PDF',
                key: 'download',
                handleSelect: () => {
                    if (!scriptRef.current) {
                        return;
                    }
                    handleQuoteDownload(
                        quote.slug,
                        scriptRef.current,
                        NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID
                    );
                    if (triggerOnboarding) {
                        handleSendActionMenuClose();
                    }
                },
            };
        },
        [quote.slug, handleSendActionMenuClose, handleQuoteDownload]
    );

    const getViewAsCustomerOption = () => {
        return {
            icon: <EyeIcon size={14} />,
            label: 'View as customer',
            key: 'view',
            handleSelect: () => window.open(props.quote.shareableViewInBrowserLink, '_blank'),
        };
    };

    const getSendViaEmailOption = React.useCallback(() => {
        return {
            icon: <EmailIcon size={14} />,
            label: 'Send via email',
            key: 'send-email',
            handleSelect: props.handleSendEmail,
        };
    }, [props.handleSendEmail]);

    const dropdownOptions = [
        getViewAsCustomerOption(),
        getCopyLinkOption(),
        getDownloadPdfOption(),
    ];

    const sendOptions = React.useMemo(() => {
        const markSentOption = {
            icon: <SendIcon size={14} />,
            label: 'Mark as sent',
            handleSelect: () => {
                handleUpdateStatus('SENT');
            },
        } as const;

        return [
            markSentOption,
            getCopyLinkOption(true),
            getDownloadPdfOption(true),
            getSendViaEmailOption(),
        ];
    }, [handleUpdateStatus, getCopyLinkOption, getDownloadPdfOption, getSendViaEmailOption]);

    const showPrimaryActionButton = getIsPrimaryActionVisible(props.quote.displayStatus);

    const actionMenuOptions =
        props.quote.__typename === 'Quote'
            ? getQuoteActionMenuOptions(
                  props.quote.id,
                  props.quote.displayStatus,
                  props.quote.sentTime,
                  setIsArchivingPopoverOpen,
                  props.handleDuplicateQuote,
                  props.handleUpdateStatus,
                  props.handleCreateInvoiceFromQuote || (() => {})
              )
            : getInvoiceActionMenuOptions(
                  props.quote.id,
                  props.quote.displayStatus,
                  setIsArchivingPopoverOpen,
                  props.handleDuplicateQuote,
                  props.handleUpdateStatus
              );

    return (
        <div className={styles['quote-overview-container']}>
            <div className='flex justify-center gap-4 flex-dir-col'>
                <Body isBold={true} size='xlarge'>
                    {props.quote.__typename === 'Quote'
                        ? `Quote ${props.quote.displayNumber}`
                        : `Invoice ${props.quote.displayNumber}`}
                </Body>
                <Body size='normal'>{props.quote.total.formattedWithCents}</Body>
            </div>
            <div className={styles['header-controls-container']}>
                {props.quote.displayStatus !== 'REVOKED' &&
                    props.quote.displayStatus !== 'ARCHIVED' && (
                        <>
                            <MoreOptionsButton
                                onPrimaryClick={() => {
                                    window.open(props.quote.shareableViewInBrowserLink, '_blank');
                                }}
                                onSecondaryClick={() => setIsViewingPopover(true)}
                                isDisabled={false}
                                primaryText={'View'}
                                primaryIcon={EyeIcon}
                                getButtonRef={viewMenuRef}
                                variant='secondary'
                            />
                            {isViewingPopover && (
                                <SelectMenu
                                    options={dropdownOptions}
                                    anchor={viewMenuRef.current}
                                    location='bottom-left'
                                    onClose={() => setIsViewingPopover(false)}
                                />
                            )}
                        </>
                    )}
            </div>
            {showPrimaryActionButton && (
                <QuotePrimaryActionButton
                    type={props.quote.__typename}
                    sendOptions={sendOptions}
                    displayStatus={props.quote.displayStatus}
                    handleUpdateStatus={props.handleUpdateStatus}
                />
            )}
            {/* @ts-expect-error */}
            <div ref={actionMenuRef} className='align-self-center pl-8'>
                <ActionMenu options={actionMenuOptions} padding='less' hasHoverBackground={true} />
            </div>
            {isArchivingPopoverOpen && (
                <Dialog
                    anchor={actionMenuRef.current}
                    question='Are you sure you want to remove? Once removed, you won’t be able to edit this quote anymore. '
                    primaryText='Yes, remove'
                    primaryVariant='danger'
                    secondaryText='No, don’t remove'
                    secondaryVariant='secondary'
                    onPrimary={() => {
                        props.handleUpdateStatus('ARCHIVED');
                        setIsArchivingPopoverOpen(false);
                    }}
                    onSecondary={() => setIsArchivingPopoverOpen(false)}
                    location='bottom'
                    customStyles={{
                        width: '240px',
                    }}
                />
            )}
            <div className={styles['quote-script-container']}>
                {/* @ts-expect-error */}
                <div id={NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID} ref={scriptRef} />
            </div>
        </div>
    );
}
